/**
 * YouTubeEmbed — lite-loading YouTube embed.
 * Accepts a full URL or an 11-char video ID and renders a click-to-load
 * poster to keep initial page weight low.
 */
import { useState } from "react";
import { Play } from "lucide-react";

const extractId = (input) => {
  if (!input) return "";
  const s = String(input).trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : "";
};

const YouTubeEmbed = ({ src, title = "Video testimonial", className = "" }) => {
  const id = extractId(src);
  const [loaded, setLoaded] = useState(false);
  if (!id) return null;
  const poster = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  return (
    <div className={`relative aspect-video rounded-xl overflow-hidden border border-border bg-black ${className}`} data-testid="youtube-embed">
      {!loaded ? (
        <button
          type="button"
          onClick={() => setLoaded(true)}
          className="absolute inset-0 group"
          aria-label={`Play ${title}`}
          data-testid="youtube-play"
        >
          <img src={poster} alt={title} loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 rounded-full bg-[hsl(var(--accent))] text-black flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform">
              <Play size={26} strokeWidth={2.2} fill="currentColor" />
            </span>
          </span>
        </button>
      ) : (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      )}
    </div>
  );
};

export default YouTubeEmbed;
